import type { CoachingQuestion } from '../../types'
import {
  hintTextStyle,
  mergeHeaderStyle,
  mergePreviewStyle,
  seriesNumberStyle,
  seriesProblemStyle,
} from '../styles'
import type { MergePhase } from '../types'
import { SourceIcon } from './source-icon'

interface MergeHeaderProps {
  primaryProblem: CoachingQuestion | null
  duplicateProblem: CoachingQuestion | null
  mergePhase: MergePhase
}

export function MergeHeader({
  primaryProblem,
  duplicateProblem,
  mergePhase,
}: MergeHeaderProps) {
  if (mergePhase === 'select-primary') {
    return (
      <div style={mergeHeaderStyle}>
        <div style={{ fontWeight: 600, marginBottom: '4px', color: '#92400e' }}>
          Link Duplicate Problems
        </div>
        <div style={hintTextStyle}>
          Step 1: Select the canonical problem (BCTCI / primary listing)
        </div>
      </div>
    )
  }

  if (mergePhase === 'select-duplicate' && primaryProblem) {
    return (
      <div style={mergeHeaderStyle}>
        <div style={{ fontWeight: 600, marginBottom: '8px', color: '#92400e' }}>
          Link Duplicate Problems
        </div>
        <div style={seriesProblemStyle}>
          <span style={{ ...seriesNumberStyle, backgroundColor: '#22c55e' }}>
            ✓
          </span>
          <SourceIcon
            sourceIcon={primaryProblem.sourceIcon}
            source={primaryProblem.source}
          />
          <span style={{ fontSize: '13px', fontWeight: 600 }}>
            {primaryProblem.externalId
              ? `#${primaryProblem.externalId} - `
              : ''}
            {primaryProblem.name}
            <span style={{ color: '#22c55e', marginLeft: '4px' }}>
              CANONICAL
            </span>
          </span>
        </div>
        <div style={hintTextStyle}>
          Step 2: Select the other source&apos;s version (same question)
        </div>
      </div>
    )
  }

  if (mergePhase === 'preview' && primaryProblem && duplicateProblem) {
    return (
      <div style={mergeHeaderStyle}>
        <div style={{ fontWeight: 600, marginBottom: '8px', color: '#92400e' }}>
          Link Preview
        </div>
        <div style={seriesProblemStyle}>
          <span style={{ ...seriesNumberStyle, backgroundColor: '#22c55e' }}>
            ✓
          </span>
          <SourceIcon
            sourceIcon={primaryProblem.sourceIcon}
            source={primaryProblem.source}
          />
          <span style={{ fontSize: '13px' }}>
            {primaryProblem.externalId
              ? `#${primaryProblem.externalId} - `
              : ''}
            {primaryProblem.name}
            <span style={{ color: '#22c55e', marginLeft: '4px' }}>
              CANONICAL
            </span>
          </span>
        </div>
        <div style={seriesProblemStyle}>
          <span style={{ ...seriesNumberStyle, backgroundColor: '#f59e0b' }}>
            ↔
          </span>
          <SourceIcon
            sourceIcon={duplicateProblem.sourceIcon}
            source={duplicateProblem.source}
          />
          <span style={{ fontSize: '13px' }}>
            {duplicateProblem.externalId
              ? `#${duplicateProblem.externalId} - `
              : ''}
            {duplicateProblem.name}
            <span style={{ color: '#f59e0b', marginLeft: '4px' }}>
              DUPLICATE
            </span>
          </span>
        </div>
      </div>
    )
  }

  return null
}

export function MergePreview() {
  return (
    <div style={mergePreviewStyle}>
      <div style={{ fontWeight: 600, marginBottom: '8px', color: '#166534' }}>
        Ready to Link
      </div>
      <div style={{ fontSize: '12px', marginBottom: '4px' }}>This will:</div>
      <ul style={{ margin: '4px 0', paddingLeft: '20px', fontSize: '12px' }}>
        <li>Mark both problems as the same question from different sources</li>
        <li>Keep each problem&apos;s content, slug, and history intact</li>
        <li>Create a reversible link you can remove later</li>
      </ul>
      <div style={hintTextStyle}>Press ⌘+Enter to confirm link</div>
    </div>
  )
}
